import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Play, Calendar } from "lucide-react";
import Footer from "../components/Footer";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Library() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      loadRecordings();
    }
  }, [currentUser]);

  const loadRecordings = async () => {
    try {
      const response = await axios.get(`${API}/recordings?user_id=${currentUser.uid}`);
      setRecordings(response.data);
    } catch (error) {
      console.error('Error loading recordings:', error);
      toast.error('Failed to load recordings');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            {/* Logo */}
            <div className="logo-placeholder"></div>
            <div className="border-l border-gray-200 pl-6 flex items-center gap-4">
              <Button
                data-testid="back-to-studio-button"
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Studio
              </Button>
              <div>
                <h1 className="text-2xl font-bold font-manrope tracking-tight text-gray-900">Recording Library</h1>
                <p className="text-sm text-gray-600 mt-1">View and manage your recordings</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-gray-600">Loading recordings...</p>
          </div>
        ) : recordings.length === 0 ? (
          <Card className="bg-white border-gray-200 shadow-md p-12" data-testid="empty-state">
            <div className="text-center">
              <Play className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h2 className="text-xl font-semibold mb-2 text-gray-900">No Recordings Yet</h2>
              <p className="text-gray-600 mb-6">Start recording in the studio to see your recordings here.</p>
              <Button
                onClick={() => navigate('/')}
                className="bg-red-600 hover:bg-red-700 shadow-md"
                data-testid="go-to-studio-button"
              >
                Go to Studio
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recordings.map((recording) => (
              <Card
                key={recording.id}
                className="bg-white border-gray-200 shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/post-production/${recording.id}`)}
                data-testid="recording-card"
              >
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <h3 className="font-semibold text-lg font-manrope text-gray-900">{recording.title}</h3>
                    <Play className="w-5 h-5 text-gray-400" />
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(recording.created_at)}</span>
                    </div>
                    
                    {recording.duration && (
                      <div className="flex items-center gap-2">
                        <span className="font-mono">Duration: {formatDuration(recording.duration)}</span>
                      </div>
                    )}
                  </div>

                  <Button
                    data-testid="edit-recording-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/post-production/${recording.id}`);
                    }}
                    className="w-full bg-gray-200 hover:bg-gray-300 border border-gray-300 text-gray-900 mt-4"
                  >
                    Edit Recording
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
}